/**
 * Canonical API-gateway base-URL resolver (Story 144.2b).
 *
 * Every HTTP client in the frontend (shared axios instance, Orval customFetch
 * mutator, notification fetch/SSE, agent SSE openers, homepage assistant)
 * resolves the gateway origin through THIS function. Do not copy this logic
 * into a new client — a second resolver is how a half-switched origin happens.
 *
 * Resolution order:
 * 1. `window._env_.API_GATEWAY_URL` — deployed environments; populated by
 *    nginx envsubst at container startup. An unsubstituted `${…}` template
 *    (local dev serves the raw file) is rejected.
 * 2. `localStorage['helios:api-gateway-url']` — local-dev escape hatch. Lets a
 *    developer point at another origin (e.g. `http://localhost:5000` while
 *    certificate trust is unavailable) without editing code. Deployed
 *    environments never reach this branch (env-config wins above).
 * 3. `https://localhost:5443` — the default local origin: the Aspire-provisioned
 *    secure gateway endpoint. TLS enables ALPN, so browsers negotiate HTTP/2
 *    and multiplex requests instead of queueing behind the 6-connections-per-
 *    origin HTTP/1.1 cap (the cause of the local document-download stall).
 *    One-time setup: `dotnet dev-certs https --trust` — see
 *    docs/development-guide.md.
 */

interface WindowEnv {
  API_GATEWAY_URL?: string;
}

declare global {
  interface Window {
    _env_?: WindowEnv;
  }
}

/** localStorage key for the local-dev gateway-origin override. */
export const API_GATEWAY_URL_OVERRIDE_KEY = 'helios:api-gateway-url';

const DEFAULT_LOCAL_GATEWAY_URL = 'https://localhost:5443';

export function getApiGatewayUrl(): string {
  if (typeof window === 'undefined') {
    return DEFAULT_LOCAL_GATEWAY_URL;
  }

  // eslint-disable-next-line no-underscore-dangle
  const envUrl = window._env_?.API_GATEWAY_URL;
  if (envUrl && !envUrl.startsWith('$')) {
    return envUrl;
  }

  try {
    const override = window.localStorage?.getItem(API_GATEWAY_URL_OVERRIDE_KEY);
    if (override) {
      return override;
    }
  } catch {
    // Privacy mode / storage access denied — fall through to the default.
  }

  return DEFAULT_LOCAL_GATEWAY_URL;
}
